import { useEffect, useMemo, useState } from "react";

function resolveValue<T>(value: T | (() => T)): T {
  return value instanceof Function ? value() : value;
}

export function useLocalStorage<T>(
  key: string,
  initialValue: T | (() => T),
) {
  const fallback = useMemo(() => resolveValue(initialValue), [initialValue]);
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === "undefined") {
      return fallback;
    }

    try {
      const rawValue = window.localStorage.getItem(key);

      if (rawValue === null) {
        return fallback;
      }

      return JSON.parse(rawValue) as T;
    } catch {
      return fallback;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== key) {
        return;
      }

      if (event.newValue === null) {
        setStoredValue(fallback);
        return;
      }

      try {
        setStoredValue(JSON.parse(event.newValue) as T);
      } catch {
        setStoredValue(fallback);
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [fallback, key]);

  const updateValue = (value: T | ((currentValue: T) => T)) => {
    setStoredValue((currentValue) => {
      const nextValue = value instanceof Function ? value(currentValue) : value;

      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, JSON.stringify(nextValue));
      }

      return nextValue;
    });
  };

  const removeValue = () => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(key);
    }
    setStoredValue(fallback);
  };

  return [storedValue, updateValue, removeValue] as const;
}
